<template>
	<p-card class="org-chart-viewer">
		<org-chart-glossary class="org-chart-viewer-glossary" :items="glossaryItem" />
		<div v-if="organizationChartData" ref="treeWrapper">
			<vue2-org-tree
				:data="organizationChartData"
				:collapsable="false"
				:label-class-name="'organization-chart-node'"
				:props="{label: 'name', children: 'details'}"
				:render-content="renderNode"
			/>
		</div>
	</p-card>
</template>

<script>
import PCard from '@/modules/shared/components/ui/Card/p-card';
import { mapState } from 'vuex';
import { head, path, pathOr, pipe, prop } from 'ramda';
import OrgChartGlossary from '@/modules/job-evaluation/components/supplies/organization-chart/OrgChartGlossary';
import Vue2OrgTree from 'vue2-org-tree';
import bus from '@/bus/app-bus';

const LOADER_EVENT = 'app-event-loader-toggle-view';
const LOADER_MESSAGE = 'Praktiko esta cargando el organigrama.';

function findNodeInTree(node, predicate) {
	if (!node) return null;
	if (predicate(node)) return node;
	if (node.details && node.details.length > 0) {
		for (let idx = 0; idx < node.details.length; idx += 1) {
			const found = findNodeInTree(node.details[idx], predicate);
			if (found) return found;
		}
	}
	return null;
}

export default {
	name: 'OrganizationChartViewer',
	components: { OrgChartGlossary, PCard, Vue2OrgTree },
	props: {
		areaId: {
			default: null,
		},
	},
	data() {
		return {
			glossaryItem: [
				{ color: '#55ac4b', label: 'LIDER eSTRATÉGICO' },
				{ color: '#f09c2b', label: 'DIRECTOR TÉCNICO DE ÁREA/PROFESIONAL' },
				{ color: '#545a54', label: 'PROFESIONAL' },
				{ color: '#e9e9e9', label: 'NO PROFESIONAL' },
			],
		};
	},
	beforeMount() {
		bus.$emit(LOADER_EVENT, { show: true, message: LOADER_MESSAGE });
		this.$store
			.dispatch('jobEvaluation/getGuidelineModelStatus')
			.finally(() => {
				bus.$emit(LOADER_EVENT, { show: false });
			});
	},
	computed: {
		...mapState('jobEvaluation', {
			fullOrganizationChart: pipe(
				pathOr([], ['supplies', 'guidelineModel', 'organizationChart']),
				head,
			),
		}),
		organizationChartData() {
			if (!this.areaId || !this.fullOrganizationChart) return this.fullOrganizationChart;

			// Search the area manager in the FULL tree. The node already carries its
			// complete reporting subtree (built by the backend from `father` relations),
			// including cross-area subordinates assigned to area members.
			const areaManager = findNodeInTree(
				this.fullOrganizationChart,
				node => node.areaId === this.areaId &&
					node.additionalInformation &&
					node.additionalInformation.isAreaManager,
			);

			if (areaManager) return areaManager;

			// Fallback: first node from the area as root
			const firstInArea = findNodeInTree(
				this.fullOrganizationChart,
				node => node.areaId === this.areaId,
			);

			return firstInArea || this.fullOrganizationChart;
		},
	},
	methods: {
		renderNode(h, nodeData) {
			const children = [
				h('span', {
					style: { backgroundColor: path(['config', 'color'], nodeData) },
					class: 'org-chart-node-type-badge',
				}),
				h('div', prop('name', nodeData)),
			];

			// When filtering by area, mark cross-area nodes (borrowed subordinates)
			if (this.areaId && nodeData.areaId && nodeData.areaId !== this.areaId) {
				children.push(h('span', { class: 'org-chart-node-area-badge' }, '[Otra área]'));
			}

			return h(
				'div',
				{ class: 'org-chart-node-content' },
				children,
			);
		},
	},
};
</script>

<style lang='scss' scoped>
.org-chart-viewer {
	padding-top: 50px !important;
	padding-bottom: 50px !important;
	position: relative;
	overflow-x: auto;
	.org-chart-viewer-glossary {
		position: absolute;
		top: 20px;
		left: 20px;
	}
}
.org-chart-viewer::-webkit-scrollbar {
	height: 8px;
}
.org-chart-viewer::-webkit-scrollbar-track {
	background: #f1f1f1;
}

.org-chart-viewer::-webkit-scrollbar-thumb {
	background: color(orange-dark);
	border-radius: 4px;
}
.org-tree-container /deep/ .org-tree {
	margin: auto;
	user-select: none;
	.organization-chart-node {
		color: var(--app-color-info);
		width: 150px;
		min-height: 80px;
		text-transform: uppercase;
		box-shadow: none !important;
		border-radius: 5px;
		padding: 0;
		position: relative;
		overflow: hidden;
		.org-chart-node-content {
			display: flex;
			justify-content: center;
			align-items: center;
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			border: solid 2px var(--app-color-gray);
		}
		.org-chart-node-type-badge {
			position: absolute;
			top: -2px;
			left: -2px;
			right: -2px;
			height: 10px;
		}
		.org-chart-node-area-badge {
			position: absolute;
			bottom: 3px;
			left: 0;
			right: 0;
			font-size: 9px;
			font-style: italic;
			font-weight: normal;
			text-transform: none;
			color: #f09c2b;
		}
	}
}
.org-tree-container {
	width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/modules/job-evaluation/components/supplies/organization-chart/OrganizationChartViewer.vue